import { promiseWithResolvers } from "@captions-web/utils/promise-with-resolvers";
import { createStore, StoreApi } from "zustand";
import { combine } from "zustand/middleware";

type InitableStore<S extends { init: () => Promise<void> }> = {
  getState: () => S;
};

interface StoreItem<T> {
  item?: T;
  promiseInfo: {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: any) => void;
  };
  init: () => Promise<void>;
}

interface RootStore {
  stores: Record<string, StoreItem<any>>;
  state: "idle" | "initializing" | "initialized";
}

export function createRootStore() {
  return createStore(
    combine({ state: "idle", stores: {} } as RootStore, (set, get) => {
      return {
        async initAll() {
          if (get().state !== "idle") {
            return;
          }

          set({ state: "initializing" });
          const stores = get().stores;
          for (const store of Object.values(stores)) {
            await store.init();
          }
          set({ state: "initialized" });
        },
        async getStorePromise<T>(name: string): Promise<T> {
          const store = get().stores[name];
          if (!store) {
            throw new Error(`Store ${name} not found`);
          }
          return store.promiseInfo.promise;
        },
        getStoreOrThrow<T>(name: string): T {
          const store = get().stores[name];
          if (!store) {
            throw new Error(`Store ${name} not found`);
          }
          return store.item;
        },
        register<T extends InitableStore<any>>(name: string, createStore: () => T) {
          if (get().stores[name]) {
            return;
          }

          const promise = promiseWithResolvers<T>();
          const init = async () => {
            const store = createStore();
            try {
              await store.getState().init();
              promise.resolve(store);

              const existingItem = get().stores[name];
              set((s) => ({ stores: { ...s.stores, [name]: { ...existingItem, item: store } } }));
            } catch (error) {
              promise.reject(error);
            }
          };
          const storeItem: StoreItem<T> = { promiseInfo: promise, init };
          set((s) => ({ stores: { ...s.stores, [name]: storeItem } }));
        },
      };
    })
  );
}

const rootStore = createRootStore();

const getRootStore = () => rootStore.getState();

export function createStoreGetters<
  S extends { init: () => Promise<void> },
  T extends InitableStore<S> = InitableStore<S>,
>(name: string) {
  return {
    getStore: () => getRootStore().getStoreOrThrow<T>(name).getState(),
    getStoreApi: () => getRootStore().getStoreOrThrow<T>(name) as unknown as StoreApi<S>,
    getStoreAsync: () =>
      getRootStore()
        .getStorePromise<T>(name)
        .then((store) => store.getState()),
  };
}

export function initializeRootStore() {
  return getRootStore().initAll();
}

export function registerStore<S extends { init: () => Promise<void> }, T extends InitableStore<S>>(
  name: string,
  createStore: () => T
) {
  getRootStore().register(name, createStore);
}
